// ============================================================
// TMR Portal Admin — contract reads and writes, over @tmr/api-client
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import type { Paged } from '@tmr/api-client';
import { companyKeys, contractKeys } from '../queryClient';
import { exportPageReader } from '../../lib/export/exportPageReader';
import { canOperate } from '../companies/status';
import type { CompanyRecord } from '../companies/contract';
import type {
  ChangeSignatoryRequest,
  ChangeSignatoryResponse,
  ContractDocument,
  ContractPartyType,
  ContractPreview,
  ContractRecord,
  ContractSigningLink,
  ContractStatus,
  CreateContractRequest,
  RequestSignatureRequest,
  RequestSignatureResponse,
} from './contract';

/** Filters the contracts list accepts; they are part of the cache key, so a change refetches. */
export interface ContractListFilters {
  partyType?: ContractPartyType;
  /** One Empresa's contracts. This is how the Empresa detail tab reads its own. */
  partyId?: string;
  status?: ContractStatus;
  page?: number;
  pageSize?: number;
}

/** How many contracts the table shows per page. */
export const CONTRACT_PAGE_SIZE = 10;

/**
 * Reads one page of the Contracts **export** (20 §3) — the same filters the table is using.
 *
 * @param filters The screen's active filters.
 */
export function contractExportPage(filters: ContractListFilters = {}) {
  return exportPageReader<ContractRecord>('/contracts/export', {
    partyType: filters.partyType,
    partyId: filters.partyId,
    status: filters.status,
  });
}

/** Behaviour {@link useContractList} accepts beyond the filters. */
export interface ContractListOptions {
  /** False keeps the query idle — for a caller whose party is not resolved yet. Defaults to true. */
  enabled?: boolean;
  /**
   * Milliseconds between automatic re-reads, or false (the default) for none.
   *
   * As a **function** it is called with the page just read and decides whether to ask again, which is how a
   * poll ends on the fact it was waiting for instead of on a timer.
   */
  refetchInterval?: number | false | ((page: Paged<ContractRecord> | undefined) => number | false);
}

/**
 * One page of contracts, already scoped by the backend to what the operator may see (06 §6).
 *
 * The server filters by party and status and orders newest first — there is no search, no sorting and no date
 * filter (09, lacuna L2). Filtering the current page in the browser is not a substitute: it would claim to
 * have searched everything while only looking at ten rows.
 *
 * @param filters Party, status and paging; every change is a new cache entry.
 * @param options Whether to run at all, and whether to keep re-reading.
 */
export function useContractList(
  filters: ContractListFilters = {},
  options: ContractListOptions = {},
): UseQueryResult<Paged<ContractRecord>> {
  const query = {
    partyType: filters.partyType,
    partyId: filters.partyId,
    status: filters.status,
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? CONTRACT_PAGE_SIZE,
  };
  const { enabled = true, refetchInterval = false } = options;

  return useQuery({
    queryKey: contractKeys.list(query),
    queryFn: ({ signal }) => api.get<Paged<ContractRecord>>('/contracts', { query, signal }),
    enabled,
    // Keeps the table on screen while the next page loads, instead of collapsing to a skeleton on every tab.
    placeholderData: (previous) => previous,
    // Same reason as the detail (`09` §11, N2): a signature lands at the provider, not here.
    refetchOnWindowFocus: true,
    refetchInterval:
      typeof refetchInterval === 'function' ? (result) => refetchInterval(result.state.data) : refetchInterval,
  });
}

/** How often the Empresa detail asks whether the contract drawn up for it has appeared. */
const AUTOMATIC_CONTRACT_POLL_MS = 3_000;

/**
 * How long it keeps asking before it stops promising.
 *
 * Sized on the backend's own cadence: the export runs on one outbox cycle (10s) and the contract is created
 * on the next, so half a minute is the ordinary case. The rest of the window is room for an export that
 * retried once.
 */
const AUTOMATIC_CONTRACT_WINDOW_MS = 150_000;

/** What the Empresa detail needs to know about the contract that is drawn up for it. */
export interface AwaitedContract {
  /** How many contracts the Empresa has, or undefined while the first read is in flight. */
  total: number | undefined;
  /** True while the automatic contract is expected and has not arrived — the screen says so and waits. */
  awaiting: boolean;
  /** True once the wait ran out. The contract may still arrive; the screen stops claiming it is imminent. */
  gaveUp: boolean;
  /** Reads the contracts again now — what the screen offers instead of asking for a page reload. */
  refresh: () => void;
}

/**
 * The Empresa's contract count, and the wait for the one the backend draws up by itself (03 §12).
 *
 * `CreateContractOnCompanyExported` creates it in the background, **one outbox cycle after the export
 * finishes** — so for the first half-minute of an Empresa's life the Contratos tab is legitimately empty,
 * and nothing on screen told that apart from "esta empresa não tem contrato". The operator either drew one
 * up by hand, which is a duplicate, or reloaded the page until it appeared. Both are rework produced by a
 * screen that knew a contract was coming and did not say so.
 *
 * The poll **stops on the fact, not on a timer**: once the count is above zero there is nothing left to
 * wait for. The window is the backstop for what the browser cannot see — a subscriber that failed, an
 * export still retrying — where asking forever would be a tab that never idles.
 *
 * @param company The Empresa, or undefined while its own read is in flight.
 */
export function useAwaitedContract(company: CompanyRecord | undefined): AwaitedContract {
  const queryClient = useQueryClient();
  const [gaveUp, setGaveUp] = useState(false);

  // The same three conditions the backend checks before creating anything: an **imported** Empresa never
  // raises `CompanyExported` (it arrived with its legacy id, so no export is ever its first), a **failed**
  // export never reaches the subscriber, and an Empresa that may not take a contract is skipped.
  const expected =
    company !== undefined &&
    company.recordOrigin === 'Onboarded' &&
    company.exportStatus !== 'Failed' &&
    canOperate(company.status);

  const query = useContractList(
    { partyId: company?.id, page: 1, pageSize: 1 },
    {
      // Without the Empresa there is no party to filter by, and an unfiltered read would count every
      // contract in the portfolio as if they were hers.
      enabled: company !== undefined,
      refetchInterval: (page) =>
        expected && !gaveUp && page?.totalCount === 0 ? AUTOMATIC_CONTRACT_POLL_MS : false,
    },
  );

  const total = query.data?.totalCount;
  const awaiting = expected && total === 0 && !gaveUp;

  useEffect(() => {
    if (!awaiting) return;
    const timer = setTimeout(() => setGaveUp(true), AUTOMATIC_CONTRACT_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [awaiting]);

  // This count is the first thing to see the contract land, and it is not the only thing showing it: the
  // Contratos tab reads its own page of the same resource and may be open in front of the operator.
  const previousTotal = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (previousTotal.current === 0 && total !== undefined && total > 0) {
      void queryClient.invalidateQueries({ queryKey: contractKeys.all });
    }
    previousTotal.current = total;
  }, [total, queryClient]);

  return {
    total,
    awaiting,
    gaveUp,
    refresh: () => void queryClient.invalidateQueries({ queryKey: contractKeys.all }),
  };
}

/** Where the contract portfolio stands — the summary above the list. */
export interface ContractCounts {
  total: number;
  pending: number;
  awaitingSignature: number;
  signed: number;
}

/**
 * The contract counts per status.
 *
 * Four count queries rather than one aggregate endpoint, exactly as `useCompanyCounts` does: each is a
 * `COUNT` plus a single row, they cache like any other query, and inventing a summary route for a number the
 * list already produces would be backend work with no other consumer.
 *
 * `pending` is the one that deserves attention: it counts contracts drawn up but never sent, which is where a
 * failed send leaves them (09, lacuna L8).
 */
export function useContractCounts(): ContractCounts {
  const total = useCountQuery();
  const pending = useCountQuery('Pending');
  const awaitingSignature = useCountQuery('AwaitingSignature');
  const signed = useCountQuery('Signed');

  return { total, pending, awaitingSignature, signed };
}

/** One count: the smallest page the server will serve, read for its `totalCount`. */
function useCountQuery(status?: ContractStatus): number {
  const query = { page: 1, pageSize: 1, status };
  const result = useQuery({
    queryKey: contractKeys.list(query),
    queryFn: ({ signal }) => api.get<Paged<ContractRecord>>('/contracts', { query, signal }),
  });
  return result.data?.totalCount ?? 0;
}

/**
 * One contract.
 *
 * A 404 here means "unknown id **or** outside your scope" — the backend refuses to tell the two apart on
 * purpose, so the screen must not imply the contract exists (06 §6).
 *
 * There is no polling, deliberately. A contract sits in `AwaitingSignature` for as long as the provider's
 * deadline allows (~3 months) and its closing arrives by webhook, so a `refetchInterval` would be a timer
 * that never idles for a fact that may not change for days — the opposite of `useOnboardingStatus`, which
 * stops precisely because its subject reaches a terminal state quickly.
 *
 * **But it does refetch when the window regains focus** (`09` §11, N2). Not polling: the signature happens
 * somewhere else — the operator goes to another window to sign, or watches the representative do it — and
 * coming back to the tab is the one moment we know something may have changed. It costs one request per
 * return, throttled by the global `staleTime`, and it is what the "status demora a mudar" complaint was
 * actually about.
 *
 * @param contractId The contract's id, or undefined while the route param is still resolving.
 */
export function useContract(contractId: string | undefined): UseQueryResult<ContractRecord> {
  return useQuery({
    queryKey: contractKeys.detail(contractId ?? ''),
    queryFn: ({ signal }) => api.get<ContractRecord>(`/contracts/${contractId}`, { signal }),
    enabled: Boolean(contractId),
    refetchOnWindowFocus: true,
  });
}

/**
 * The contract's document, as the provider currently describes it — name, status, deadline, download links
 * and the full event history.
 *
 * **Deliberately uncached** (`staleTime: 0`, `gcTime: 0`). The download links expire within minutes, so a
 * cached copy is a set of links that stop working with nothing on screen explaining why. The backend answers
 * `no-store` for the same reason. This is the one query in the portal that must always go to the network.
 *
 * @param contractId The contract whose document to read.
 * @param enabled Pass false when the contract has no document yet — a `Pending` contract answers 404, and
 *   asking for something the caller already knows is absent turns a normal state into an error on screen.
 */
export function useContractDocument(
  contractId: string | undefined,
  enabled = true,
): UseQueryResult<ContractDocument> {
  return useQuery({
    queryKey: contractKeys.document(contractId ?? ''),
    queryFn: ({ signal }) => api.get<ContractDocument>(`/contracts/${contractId}/document`, { signal }),
    enabled: Boolean(contractId) && enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * What sending this contract would produce: every value the document would print, who would be asked to
 * sign, and everything that is wrong or missing.
 *
 * **Uncached, like the document read**, for the opposite reason: it is not that the payload expires, but
 * that it describes the Empresa's record *right now*. An operator who opens this, fixes the cadastre and
 * comes back has to see the correction — a cached preview would show them the document they just stopped
 * sending, which is the exact mistake this screen exists to prevent.
 *
 * A blocking issue is **not** an error: the response is a 200 carrying `ready: false` and the reason, so the
 * screen shows what to fix instead of an error state with nothing in it.
 *
 * @param contractId The contract to preview.
 * @param enabled Pass false while the modal is closed — this resolves the Empresa on every call.
 */
export function useContractPreview(
  contractId: string | undefined,
  enabled = true,
): UseQueryResult<ContractPreview> {
  return useQuery({
    queryKey: contractKeys.preview(contractId ?? ''),
    queryFn: ({ signal }) => api.get<ContractPreview>(`/contracts/${contractId}/preview`, { signal }),
    enabled: Boolean(contractId) && enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Discards a contract that was never sent.
 *
 * The backend accepts this for `Pending` and nothing else: once the document exists it is in somebody's
 * inbox with a live signing link, so that cancellation belongs at the provider. See `canCancel` in
 * `./status`, which mirrors the same guard.
 */
export function useCancelContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, reason }: { contractId: string; reason: string | null }) =>
      api.post<ContractRecord>(`/contracts/${contractId}/cancel`, { reason }),
    onSuccess: (contract) => {
      void queryClient.invalidateQueries({ queryKey: contractKeys.all });
      // The Empresa's tab counts its contracts and its badge reads the same lifecycle.
      void queryClient.invalidateQueries({ queryKey: companyKeys.detail(contract.partyId) });
    },
  });
}

/**
 * Draws up a contract for an Empresa. It sends nothing for signature — that is {@link useRequestSignature},
 * a separate call, so the contract exists in our own database before an external provider is involved.
 */
export function useCreateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateContractRequest) =>
      api.post<ContractRecord>('/contracts', request),
    onSuccess: (_contract, request) => {
      void queryClient.invalidateQueries({ queryKey: contractKeys.all });
      // The Empresa's detail carries its contract count and its own status badge.
      void queryClient.invalidateQueries({ queryKey: companyKeys.detail(request.partyId) });
    },
  });
}

/**
 * Sends a contract to its signature provider.
 *
 * The contract id is an argument of the mutation, not of the hook — a deliberate departure from
 * `useUpdateCompany(companyId)`. Neither caller knows the id at render time: the table picks a row on click,
 * and the creation modal only learns the id after step one succeeds.
 *
 * **A failure here leaves the contract untouched and re-sendable.** `RequestSignatureHandler` calls the
 * provider before it writes anything, so a 502 means the contract is still `Pending` — the caller must say
 * so rather than implying the whole operation was lost.
 *
 * The channel travels with the id for the same reason the id does: it is the operator's answer to the
 * selector in the modal, so it is not known until they click.
 */
export function useRequestSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, ...request }: { contractId: string } & RequestSignatureRequest) =>
      api.post<RequestSignatureResponse>(`/contracts/${contractId}/signature`, request),
    onSuccess: (_response, { contractId }) => {
      void queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) });
      void queryClient.invalidateQueries({ queryKey: contractKeys.all });
      // Not housekeeping: sending moves the Empresa to AwaitingSignature, and a signed contract completes it
      // through a subscriber (M3 Fase 4). Without this the Empresa's badge lies on the screen next door.
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
    },
  });
}

/**
 * The representative's signing link, read live from the provider (`GET /contracts/{id}/signing-link`).
 *
 * **Never cached**, like the document read: replacing the signatory invalidates the link, so a copy served
 * from cache could be one that no longer opens anything. Enabled only while the dialog that shows it is
 * open — every call goes to the provider.
 *
 * @param contractId The contract whose link to read.
 * @param enabled Pass false while nobody is looking at it.
 */
export function useSigningLink(
  contractId: string | undefined,
  enabled = true,
): UseQueryResult<ContractSigningLink> {
  return useQuery({
    queryKey: contractKeys.signingLink(contractId ?? ''),
    queryFn: ({ signal }) => api.get<ContractSigningLink>(`/contracts/${contractId}/signing-link`, { signal }),
    enabled: Boolean(contractId) && enabled,
    staleTime: 0,
    gcTime: 0,
    // A 409 (the representative just signed, the document closed) is an answer, not a blip to retry.
    retry: false,
  });
}

/**
 * Sends a contract's signature request again to the same signatory, on the channel they were set up with.
 *
 * Nothing about the contract changes, but the document's history at the provider does (a new notification).
 */
export function useResendSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId }: { contractId: string }) =>
      api.post<ContractRecord>(`/contracts/${contractId}/signature/resend`),
    onSuccess: (_contract, { contractId }) => {
      // The contract's key is a prefix of its document's, so the history picks up the new notification.
      void queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) });
    },
  });
}

/**
 * Puts a contract that is out for signature in front of a corrected phone or e-mail — same document, new
 * signatory (02 §7).
 *
 * **A failure leaves the contract as it was**: the provider is called before anything is recorded, so a
 * 502 means the old contact is still the one on the contract, and the call can be repeated.
 */
export function useChangeSignatory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, ...request }: { contractId: string } & ChangeSignatoryRequest) =>
      api.post<ChangeSignatoryResponse>(`/contracts/${contractId}/signatory`, request),
    onSuccess: () => {
      // The whole family — the row behind the drawer shows the same "Enviado por" contact, and the old
      // signing link and document history under the contract's key are stale too.
      void queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}
